import { useCallback, useEffect, useRef } from 'react';
import { Control, useFieldArray, UseFormWatch } from 'react-hook-form';
import { buildInstallments, parseConditionToOffsets } from '../../OrderForm/utils';
import { NotaPagamentoFormValues } from './Sections/notaPagamento.schema';

interface UseNotaInstallmentsParams {
  control: Control<NotaPagamentoFormValues>;
  watch: UseFormWatch<NotaPagamentoFormValues>;
  total: number;
}

/**
 * Gera as parcelas a partir da condição escolhida — nunca na hidratação (`marcarHidratando`
 * arma o próximo `reset` para ser ignorado) nem quando o total muda por outro motivo, já que
 * a nota reabre várias vezes (polling de status, re-sync pós-emissão) e regenerar apagaria a
 * personalização feita na tela.
 */
export const useNotaInstallments = ({ control, watch, total }: UseNotaInstallmentsParams) => {
  const { fields, replace, update } = useFieldArray({ control, name: 'installments' });

  const paymentConditionId = watch('paymentConditionId');
  const paymentConditionName = watch('paymentConditionName');
  const lastConditionId = useRef(paymentConditionId);
  const totalRef = useRef(total);
  totalRef.current = total;

  useEffect(() => {
    const conditionChanged = paymentConditionId !== lastConditionId.current;
    lastConditionId.current = paymentConditionId;
    if (!conditionChanged || !paymentConditionId) return;

    const offsets = parseConditionToOffsets(paymentConditionName);
    replace(buildInstallments(totalRef.current, offsets));
  }, [paymentConditionId, paymentConditionName, replace]);

  // a hidratação registra a condição que o `reset` vai colocar, em vez de armar um "ignore o
  // próximo disparo": a nota não persiste condição, então o reset costuma não mudar o campo,
  // o efeito não roda, e a flag armada engoliria a primeira escolha do usuário
  const marcarHidratando = useCallback((conditionIdHidratado?: number) => {
    lastConditionId.current = conditionIdHidratado;
  }, []);

  const installments = watch('installments') || [];
  const sumInstallments = installments.reduce((acc, parcela) => acc + (Number(parcela?.valor) || 0), 0);
  const hasMismatch = fields.length > 0 && Math.abs(sumInstallments - total) > 0.01;

  return { fields, replace, update, marcarHidratando, sumInstallments, hasMismatch };
};
